import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Lightbulb } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'

interface LogisticsRow {
  id: string
  showId: string
  category: string
  description: string
  ownEstimatePence?: number
  rentEstimatePence?: number
  carnetRequired: boolean
  decision?: string
  recommendation?: string
  createdAt: string
  show?: { title: string; date: string }
}

export function LogisticsPage() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const scope = usePerformanceScope()
  const { data, error, isLoading } = useQuery<LogisticsRow[], ApiClientError>({
    queryKey: ['logistics', ...scope.queryKeySuffix],
    queryFn: () => artistFetch<LogisticsRow[]>(`/logistics${scope.toQueryString()}`),
  })
  const rows = scope.filterByShowIds(data ?? [], (r) => r.showId)

  async function getRecommendation(id: string) {
    await artistFetch(`/logistics/${id}/recommend-decision`, { method: 'POST' })
    qc.invalidateQueries({ queryKey: ['logistics'] })
  }

  return (
    <div>
      <PageHeader
        title="Equipment Logistics"
        subtitle="Own vs rent decisions, freight tracking, and supplier contacts."
        actions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'Cancel' : 'Add item'}
          </button>
        }
      />
      <PerformanceScopeBanner />
      {adding && (
        <AddLogisticsForm
          onCreated={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['logistics'] })
          }}
        />
      )}
      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={rows}
          rowKey={(r) => r.id}
          emptyMessage="No logistics items recorded."
          columns={[
            {
              key: 'category',
              header: 'Category',
              render: (r) => <span className="uppercase text-xs font-medium">{r.category}</span>,
            },
            { key: 'description', header: 'Description', render: (r) => r.description },
            {
              key: 'show',
              header: 'Show',
              render: (r) => r.show?.title ?? r.showId.slice(0, 8),
            },
            {
              key: 'own',
              header: 'Own est.',
              align: 'right',
              render: (r) =>
                r.ownEstimatePence != null ? `£${(r.ownEstimatePence / 100).toFixed(2)}` : '—',
            },
            {
              key: 'rent',
              header: 'Rent est.',
              align: 'right',
              render: (r) =>
                r.rentEstimatePence != null ? `£${(r.rentEstimatePence / 100).toFixed(2)}` : '—',
            },
            {
              key: 'carnet',
              header: 'Carnet',
              render: (r) => r.carnetRequired ? 'Yes' : 'No',
            },
            {
              key: 'decision',
              header: 'Decision',
              render: (r) =>
                r.decision ? (
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${r.decision === 'own' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>
                    {r.decision.toUpperCase()}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => getRecommendation(r.id)}
                    className="flex items-center gap-1 text-xs text-blue-700 hover:underline"
                  >
                    <Lightbulb className="h-3 w-3" />
                    Recommend
                  </button>
                ),
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}

function AddLogisticsForm({ onCreated }: { onCreated: () => void }) {
  const [showId, setShowId] = useState('')
  const [category, setCategory] = useState('pa')
  const [description, setDescription] = useState('')
  const [ownEstimate, setOwnEstimate] = useState('')
  const [rentEstimate, setRentEstimate] = useState('')
  const [carnetRequired, setCarnetRequired] = useState(false)
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      await artistFetch('/logistics', {
        method: 'POST',
        body: {
          showId,
          category,
          description,
          ownEstimatePence: ownEstimate ? Math.round(Number(ownEstimate) * 100) : undefined,
          rentEstimatePence: rentEstimate ? Math.round(Number(rentEstimate) * 100) : undefined,
          carnetRequired,
        },
      })
      onCreated()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Show ID</label>
          <input required value={showId} onChange={(e) => setShowId(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="UUID of the show" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm">
            <option value="lighting">Lighting</option>
            <option value="pa">PA</option>
            <option value="backline">Backline</option>
            <option value="staging">Staging</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Description</label>
          <input required value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="e.g. D&B V-Series line array" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Own estimate (major units)</label>
          <input type="number" step="0.01" value={ownEstimate} onChange={(e) => setOwnEstimate(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Rent estimate (major units)</label>
          <input type="number" step="0.01" value={rentEstimate} onChange={(e) => setRentEstimate(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 flex items-center gap-2 pt-5 text-xs uppercase tracking-wider text-ovation-muted">
            <input type="checkbox" checked={carnetRequired} onChange={(e) => setCarnetRequired(e.target.checked)} />
            ATA Carnet required
          </label>
        </div>
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Creating...' : 'Create item'}
      </button>
    </form>
  )
}
